import { LOCATION_BATCH_MAX, type LocationPingBatchEntryDto } from '@hrms/shared';

/**
 * Positions waiting to be sent, kept where a reload cannot lose them.
 *
 * The free plugin hands each position to JavaScript and keeps nothing itself,
 * so if this app forgets one it is gone — there is no native queue underneath
 * to fall back on. Hence localStorage rather than memory: a WebView that Android
 * tears down and rebuilds must come back to the same queue.
 *
 * Keyed per user because a shared handset would otherwise send one person's
 * positions under whoever logged in next.
 */
function queueKey(userId: string): string {
  return `kiobytes-hr:location-queue:${userId}`;
}

/**
 * How many positions to hold before dropping the oldest.
 *
 * A day of reporting, roughly. Past that the phone has been offline long enough
 * that the server's 48-hour window will refuse the oldest of them anyway, and an
 * unbounded queue in localStorage eventually throws a quota error that would
 * take the reporter down with it.
 */
const QUEUE_LIMIT = 500;

/**
 * A queued position and the local id used to remove it again.
 *
 * The id is the whole point. Removing by *count* was wrong: the queue can move
 * between the moment a batch is read and the moment the server accepts it —
 * positions arrive, and at the ceiling each arrival evicts one from the head —
 * so a count computed before an upload deletes a different set afterwards,
 * throwing away positions that were never sent. Removing by id cannot do that
 * however the queue has shifted underneath.
 */
export interface QueuedPosition {
  qid: string;
  ping: LocationPingBatchEntryDto;
}

let counter = 0;

function newId(): string {
  // randomUUID needs a secure context; the app is https, but a WebView with an
  // older engine can still lack it, and a queue that throws here would stop
  // reporting entirely. The fallback only has to be unique within one device.
  const uuid = globalThis.crypto?.randomUUID?.();
  if (uuid) return uuid;

  counter += 1;
  return `q${Date.now().toString(36)}-${counter}`;
}

function read(userId: string): QueuedPosition[] {
  try {
    const raw = window.localStorage.getItem(queueKey(userId));
    if (!raw) return [];

    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    // Entries written before ids existed are bare pings. Adopting them rather
    // than discarding them means an upgrade does not silently bin a backlog.
    return parsed.flatMap((entry) => {
      if (typeof entry !== 'object' || entry === null) return [];

      const candidate = entry as Partial<QueuedPosition> & Partial<LocationPingBatchEntryDto>;
      if (typeof candidate.qid === 'string' && candidate.ping) {
        return [{ qid: candidate.qid, ping: candidate.ping }];
      }

      if (typeof candidate.recordedAt === 'string') {
        return [{ qid: newId(), ping: entry as LocationPingBatchEntryDto }];
      }

      return [];
    });
  } catch {
    // Corrupt or unavailable storage must not stop reporting. An empty queue
    // loses the backlog; a thrown error loses everything after it too.
    return [];
  }
}

function write(userId: string, entries: QueuedPosition[]): void {
  try {
    window.localStorage.setItem(queueKey(userId), JSON.stringify(entries));
  } catch {
    // Full or disabled storage. Nothing useful to do, and the position in hand
    // is worth less than the reporter staying alive.
  }
}

/** Adds a position, dropping the oldest if the queue is already full. */
export function enqueue(userId: string, ping: LocationPingBatchEntryDto): void {
  const entries = read(userId);
  entries.push({ qid: newId(), ping });
  write(userId, entries.slice(-QUEUE_LIMIT));
}

/** The next page to send, oldest first. */
export function peekBatch(userId: string): QueuedPosition[] {
  return read(userId).slice(0, LOCATION_BATCH_MAX);
}

/**
 * Drops exactly the positions the server took responsibility for.
 *
 * By id, never by count, and re-reading the queue as it stands now — anything
 * that arrived during the upload is left alone.
 *
 * Called only after a 200. The server answers 200 even when it stored nothing:
 * a position taken after clocking out is refused permanently, and a queue that
 * kept retrying it would never drain. So "accepted" is not the test. "The server
 * has seen these" is.
 */
export function dropSent(userId: string, qids: string[]): void {
  if (qids.length === 0) return;

  const sent = new Set(qids);
  write(
    userId,
    read(userId).filter((entry) => !sent.has(entry.qid)),
  );
}

/**
 * How many are waiting, in full.
 *
 * No screen shows this any more — My Phones lists devices and nothing else —
 * so its remaining caller is the spec, where it is the only way to assert a
 * queue longer than one page: `peekBatch` stops at LOCATION_BATCH_MAX and
 * cannot see the 500-entry ceiling it is meant to prove.
 */
export function queueLength(userId: string): number {
  return read(userId).length;
}

/** Forgets everything queued for this user, on revocation or when tracking stops. */
export function clearQueue(userId: string): void {
  try {
    window.localStorage.removeItem(queueKey(userId));
  } catch {
    // Nothing to do.
  }
}
